import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Card,
    Typography,
    TextField,
    Fab,
    Grid,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
    Tooltip,
    Alert,
    Button,
    IconButton
} from '@mui/material';
import { Login, Logout, ErrorOutline, Close, Check as CheckIcon, ExitToApp } from '@mui/icons-material';
import theme from '../../../../assets/theme';
import { MobileTimePicker } from '@mui/x-date-pickers/MobileTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useNotification } from '../../../../context/notification.context';
import { useLoading } from "../../../../context/loading.context";
import { checkInReservation, checkOutReservation, estimatetariff, getUnallocatedAmount, updateReservation } from '../../../../service/network';
import CustomButton from '../../../../components/Buttons';
import { useNavigate } from 'react-router-dom';

export default function ReservationQuickPopup({ open, onClose, reservation, refreshData }) {
    const [hour, setHour] = useState('00');
    const [minute, setMinute] = useState('00');
    const [date, setDate] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const [openActionDialog, setOpenActionDialog] = useState(false);
    const navigate = useNavigate();
    const { showNotification } = useNotification();
    const { showLoader, hideLoader } = useLoading();
    const [unallocatedAmount, setUnallocatedAmount] = useState(2);
    const [estimates, setEstimates] = useState(null);

    console.log("reservation", reservation)
    useEffect(() => {
        if (reservation) {
            const today = new Date();
            const formattedDate = today.toISOString().split('T')[0];

            // Initialize with current date/time by default
            setDate(formattedDate);
            setHour(String(today.getHours()).padStart(2, '0'));
            setMinute(String(today.getMinutes()).padStart(2, '0'));

            // Set pending action based on status
            if (reservation.reservation_status === 'Checked-In') {
                setPendingAction('checkOut');
            } else {
                setPendingAction('checkIn');
            }
        }
    }, [reservation]);


    const handleViewMore = () => {
        console.log("reservation room", reservation)
        navigate(`/reservation`, {
            
            state: { reservationId: reservation.reservation_id, details: reservation }
            
        });
        onClose();
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return dayjs(dateString).format('DD-MM-YYYY');
    };


    useEffect(() => {
        if (reservation?.reservation_id) {
            getUnallocatedAmount({ reservation: reservation?.reservation_id })
                .then((res) => {
                    setUnallocatedAmount(res.data.message.unallocated);
                })
                .catch((err) => {
                    console.error('Error fetching unallocated amount:', err);
                    setUnallocatedAmount(0);
                });
        }
    }, [reservation?.reservation_id]);


    useEffect(() => {
        if (reservation?.reservation_id) {
            estimatetariff({ reservation_id: reservation?.reservation_id }).then((resp) => {
                setEstimates(resp.data.message);
            }).catch((err) => {
                console.log(err);
            });
        }
    }, [reservation?.reservation_id]);

    const extractApiErrorMessage = (error, defaultMessage = 'An error occurred') => {
        if (error?.response?.data?._server_messages) {
            try {
                const messages = JSON.parse(error.response.data._server_messages);
                return messages.map(msg => {
                    try {
                        const parsed = JSON.parse(msg);
                        return parsed.message?.replace(/<[^>]+>/g, '') || msg;
                    } catch {
                        return msg;
                    }
                }).join(', ');
            } catch (e) {
                console.error('Failed to parse _server_messages:', e);
            }
        }

        if (error?.response?.data?.message) {
            return error.response.data.message;
        }

        return error.message || defaultMessage;
    };

    const handleCheckIn = async () => {
        setIsLoading(true);
        showLoader(true);

        try {
            const checkInDateTime = `${date} ${hour}:${minute}:00`;

            const payload = {
                room: reservation.room,
                name: reservation.reservation_id,
                check_in: checkInDateTime,
            };

            const response = await checkInReservation(payload);

            if (response?.status === 200 || response?.data?.success) {
                refreshData();
                onClose();
                showNotification('success', 'Success', response?.data?.message || 'Check-in successful');
            } else {
                throw new Error(response?.data?.message || 'Failed to check in');
            }
        } catch (error) {
            console.error('Check-in error:', error);
            const errorMessage = extractApiErrorMessage(error, 'Failed to check in');
            showNotification('error', 'Error', errorMessage);
        } finally {
            setIsLoading(false);
            hideLoader();
        }
    };

    const handleCheckOut = async () => {
        setIsLoading(true);
        showLoader(true);

        try {
            const checkOutDateTime = `${date} ${hour}:${minute}:00`;

            const payload = {
                room: reservation.room,
                name: reservation.reservation_id,
                check_out: checkOutDateTime,
            };

            const response = await checkOutReservation(payload);

            if (response?.status === 200 || response?.data?.success) {
                refreshData();
                onClose();
                showNotification('success', 'Success', response?.data?.message || 'Check-out successful');
            } else {
                throw new Error(response?.data?.message || 'Failed to check out');
            }
        } catch (error) {
            console.error('Check-out error:', error);
            const errorMessage = extractApiErrorMessage(error, 'Failed to check out');
            showNotification('error', 'Error', errorMessage);
        } finally {
            setIsLoading(false);
            hideLoader();
        }
    };

    const handleCancelReservation = async () => {
        setIsLoading(true);
        showLoader(true);

        try {
            const payload = {
                status: 'Cancelled'
            };

            const response = await updateReservation(reservation.reservation_id, payload);

            if (response?.status === 200 || response?.data?.data) {
                showNotification('success', 'Success', 'Reservation cancelled successfully');
                refreshData();
                onClose();
            } else {
                throw new Error(response?.data?.message || 'Failed to cancel reservation');
            }
        } catch (error) {
            console.error('Cancel reservation error:', error);
            const errorMessage = extractApiErrorMessage(error, 'Failed to cancel reservation');
            showNotification('error', 'Error', errorMessage);
        } finally {
            setIsLoading(false);
            hideLoader();
            setShowCancelConfirmation(false);
        }
    };

    const handleConfirmation = () => {
        setOpenActionDialog(false);
        if (pendingAction === 'checkIn') {
            handleCheckIn();
        } else if (pendingAction === 'checkOut') {
            handleCheckOut();
        }
    };

    const getStatusText = () => {
        switch (reservation?.reservation_status) {
            case 'Checked-Out': return 'Checked Out';
            case 'Checked-In': return 'Checked In';
            case 'Cancelled': return 'Cancelled';
            default: return 'Ready for Check-in';
        }
    };

    const getStatusColor = () => {
        switch (reservation?.reservation_status) {
            case 'Checked-In': return theme.palette.error.main;
            case 'Checked-Out': return theme.palette.primary.main;
            case 'Cancelled': return theme.palette.grey[500];
            default: return theme.palette.primary.main;
        }
    };

    const getFabColor = () => {
        switch (reservation?.reservation_status) {
            case 'Checked-In': return 'error';
            default: return 'primary';
        }
    };

    const getFabIcon = () => {
        switch (reservation?.reservation_status) {
            case 'Checked-In': return <Logout />;
            default: return <Login />;
        }
    };

    const handleFabClick = () => {
        setOpenActionDialog(true);
    };

    const isFabDisabled = () => {
        if (isLoading) return true;
        if (!reservation?.room_no || !reservation?.reservation_id) return true;
        if (reservation.reservation_status === 'Cancelled') return true;
        return false;
    };

    const hasCheckoutIssues = useMemo(() => {
        if (pendingAction !== 'checkOut') return false;

        // Check if checkout date is not the current date
        const checkoutDate = dayjs(date).format('YYYY-MM-DD');
        const currentDate = dayjs().format('YYYY-MM-DD');
        const isNotCurrentDate = checkoutDate !== currentDate;

        // Check if payment issues exist
        const amountDue = (reservation?.invoiced_amount ?? 0) - (reservation?.paid_amount ?? 0);
        const hasPaymentIssues = amountDue > 0 || (reservation?.unallocated_amount ?? 0) > 0;

        return { isNotCurrentDate, hasPaymentIssues };
    }, [pendingAction, date, reservation]);

    const getDialogContent = () => {
        const actionTime = `${date} ${hour}:${minute}`;
        const amountDue = (reservation?.invoiced_amount ?? 0) - (reservation?.paid_amount ?? 0);
        console.log("estimates", estimates)

        return (
            <Box sx={{
                mt: 2,
                '& .MuiGrid-item': {
                    display: 'flex',
                    flexDirection: 'column'
                }
            }}>

                {/* Header with icon */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 3,
                    p: 2,
                    backgroundColor: pendingAction === 'checkIn'
                        ? 'rgba(25, 118, 210, 0.08)'
                        : 'rgba(211, 47, 47, 0.08)',
                    borderRadius: 2,
                    borderLeft: `4px solid ${pendingAction === 'checkIn'
                        ? theme.palette.primary.main
                        : theme.palette.error.main}`
                }}>
                    {pendingAction === 'checkIn' ? (
                        <Login sx={{
                            color: theme.palette.primary.main,
                            fontSize: 32,
                            mr: 2
                        }} />
                    ) : (
                        <Logout sx={{
                            color: theme.palette.error.main,
                            fontSize: 32,
                            mr: 2
                        }} />
                    )}
                    <Box>
                        <Typography variant="h6" fontWeight="600">
                            {pendingAction === 'checkIn' ? 'Confirm Check-In' : 'Confirm Check-Out'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {pendingAction === 'checkIn'
                                ? 'Please verify the check-in details'
                                : 'Please review the payment information'}
                        </Typography>
                    </Box>
                </Box>

                {/* Reservation Info Card */}
                <Box sx={{
                    p: 2,
                    mb: 3,
                    borderRadius: 2,
                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }
                }}>
                    <Typography variant="subtitle1" fontWeight="500" gutterBottom>
                        Reservation Details
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid size={3.5}>
                            <Typography variant="caption" color="text.secondary">
                                RESERVATION ID
                            </Typography>
                            <Typography variant="body1" fontWeight="500">
                                {reservation?.reservation_id || 'N/A'}
                            </Typography>
                        </Grid>
                        <Grid size={3.5}>
                            <Typography variant="caption" color="text.secondary">
                                ROOM NUMBER
                            </Typography>
                            <Typography variant="body1" fontWeight="500">
                                {reservation?.room_no || 'N/A'}

                            </Typography>
                        </Grid>
                        <Grid size={5}>
                            <Typography variant="caption" color="text.secondary">
                                BOOKED DATES
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip
                                    label={formatDate(reservation?.arrival_date)}
                                    variant="outlined"
                                    color="primary"
                                />
                                <Typography variant="body1">to</Typography>
                                <Chip
                                    label={formatDate(reservation?.departure_date)}
                                    variant="outlined"
                                    color="primary"
                                />
                                {pendingAction === 'checkOut' && hasCheckoutIssues.isNotCurrentDate && (
                                    <Tooltip title="Check-out date is not today's date">
                                        <ErrorOutline color="error" fontSize="small" />
                                    </Tooltip>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                {/* Time Information */}
                <Box sx={{
                    p: 2,
                    mb: 3,
                    borderRadius: 2,
                    backgroundColor: pendingAction === 'checkIn'
                        ? 'rgba(25, 118, 210, 0.05)'
                        : 'rgba(211, 47, 47, 0.05)',
                    border: `1px solid ${pendingAction === 'checkIn'
                        ? 'rgba(25, 118, 210, 0.3)'
                        : 'rgba(211, 47, 47, 0.3)'}`,
                    transition: 'all 0.3s ease'
                }}>
                    <Typography variant="subtitle1" fontWeight="500" gutterBottom>
                        {pendingAction === 'checkIn' ? 'Check-In Time' : 'Check-Out Time'}
                    </Typography>

                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 2,
                        backgroundColor: 'white',
                        borderRadius: 1,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                    }}>
                        <Typography variant="body1" color="text.secondary">
                            {pendingAction === 'checkIn' ? 'Check-in at:' : 'Check-out at:'}
                        </Typography>
                        <Typography variant="h6"
                            color={pendingAction === 'checkIn' ? 'primary.main' : 'error.main'}
                            fontWeight="600">
                            {actionTime}
                        </Typography>
                    </Box>
                </Box>

                {/* Payment Information (only for check-out) */}
                {pendingAction === 'checkOut' && (
                    <Box sx={{
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: 'rgba(101, 101, 101, 0.05)',
                        border: '1px solid rgba(0, 0, 0, 0.1)'
                    }}>
                        <Typography variant="subtitle1" fontWeight="500" gutterBottom>
                            Payment Summary
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid size={4}>
                                <Typography variant="caption" color="text.secondary">
                                    INVOICE AMOUNT
                                </Typography>
                                <Typography variant="body1" fontWeight="500">
                                    {reservation.invoiced_amount}
                                </Typography>
                            </Grid>
                            <Grid size={4}>
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <Typography variant="caption" color="text.secondary">
                                        AMOUNT DUE
                                    </Typography>
                                    {(amountDue != 0) && (
                                        <Tooltip title="Outstanding payment due">
                                            <ErrorOutline color="error" fontSize="small" />
                                        </Tooltip>
                                    )}
                                </Box>
                                <Typography variant="body1"
                                    color={amountDue != 0 ? 'error.main' : 'success.main'}
                                    fontWeight="600">
                                    {`₹ ${amountDue}`}
                                </Typography>
                            </Grid>
                            <Grid size={4}>
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <Typography variant="caption" color="text.secondary">
                                        UNALLOCATED AMOUNT
                                    </Typography>
                                    {unallocatedAmount > 0 && (
                                        <Tooltip title="Unallocated amount exists">
                                            <ErrorOutline color="error" fontSize="small" />
                                        </Tooltip>
                                    )}
                                </Box>
                                <Typography variant="body1" fontWeight="500"
                                    color={unallocatedAmount > 0 ? 'error.main' : 'text.primary'}>
                                    ₹{unallocatedAmount}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>
                )}

                {/* Footer Note */}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
                    {pendingAction === 'checkIn'
                        ? 'This action will mark the guest as checked-in'
                        : 'This action will finalize the check-out process'}
                </Typography>
            </Box>
        );
    };

    if (!reservation) return null;

    return (
        <>
            {/* Main Quick Popup */}
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                    },
                }}
            >
                <Box sx={{ bgcolor: "light.main" }}>
                    <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="headingLarge">
                            Quick Actions - {reservation.reservation_id}  | {reservation.guest_name}
                        </Typography>
                        <Button
                            endIcon={<ExitToApp />}
                            size="small"
                            onClick={handleViewMore}
                            sx={{
                                textTransform: 'none',
                                border: '2px solid #27394D',
                                borderRadius: theme.borderRadius.medium,
                                display: 'flex',
                                backgroundColor: "primary.main",
                                color: "light.main",
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                p: 1,
                            }}
                        >
                            <Typography variant="subHeading">
                                View More
                            </Typography>

                        </Button>
                    </DialogTitle>

                    <DialogContent>
                        <Grid container spacing={2}>
                            <Grid size={12}>
                                <Card
                                    sx={{
                                        p: 2,
                                        borderRadius: 3,
                                        backgroundColor: 'light.main',
                                        height: '100%',
                                    }}
                                >
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                        <Typography variant="h6" fontWeight="bold" mb={2}>
                                            {getStatusText()}
                                        </Typography>
                                  
                                    </Box>

                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Typography variant="body2" mb={0.5}>Date</Typography>
                                            <TextField
                                                type="date"
                                                fullWidth
                                                value={date}
                                                onChange={(e) => setDate(e.target.value)}
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        borderRadius: theme.borderRadius.medium,
                                                        backgroundColor: theme.palette.lightgray.main,
                                                    }
                                                }}
                                            />
                                        </Grid>

                                        <Grid item xs={6}>
                                            <Typography variant="body2" mb={0.5}>Time</Typography>
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                <MobileTimePicker
                                                    value={dayjs().hour(hour).minute(minute)}
                                                    onChange={(newValue) => {
                                                        if (newValue) {
                                                            console.log("newValue",newValue);
                                                            setHour(String(newValue.hour()).padStart(2, '0'));
                                                            setMinute(String(newValue.minute()).padStart(2, '0'));
                                                        }
                                                    }}
                                                    ampm={false}
                                                    minutesStep={1}
                                                    slotProps={{
                                                        textField: {
                                                            variant: 'outlined',
                                                            sx: {
                                                                width: '100%',
                                                                "& .MuiOutlinedInput-root": {
                                                                    borderRadius: `${theme.borderRadius.medium} !important`,
                                                                },
                                                                "& .MuiOutlinedInput-notchedOutline": {
                                                                    borderColor: theme.palette.grey[400],
                                                                },
                                                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                                                    borderColor: theme.palette.primary.main,
                                                                },
                                                            },
                                                        }
                                                    }}
                                                />
                                            </LocalizationProvider>
                                        </Grid>

                                        <Grid item xs={6}>
                                            <Typography variant="body2" mb={0.5}>Room No</Typography>
                                            <TextField
                                                fullWidth
                                                value={reservation?.room_no}
                                                disabled
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        borderRadius: theme.borderRadius.medium,
                                                        backgroundColor: theme.palette.lightgray.main,
                                                    }
                                                }}
                                            />
                                        </Grid>

                                        <Grid item xs={4} mt={2.5}>
                                            {reservation?.reservation_status !== 'Cancelled' && (
                                                <Fab
                                                    color={getFabColor()}
                                                    sx={{
                                                        backgroundColor: getStatusColor(),
                                                        color: '#fff',
                                                        boxShadow: `0px 4px 12px ${getStatusColor()}40`,
                                                        '&:hover': {
                                                            backgroundColor: getStatusColor(),
                                                            opacity: 0.9
                                                        },
                                                        '&:disabled': {
                                                            backgroundColor: theme.palette.grey[400],
                                                        }
                                                    }}
                                                    onClick={handleFabClick}
                                                    disabled={isFabDisabled()}
                                                >
                                                    {isLoading ? (
                                                        <CircularProgress size={24} color="inherit" />
                                                    ) : (
                                                        getFabIcon()
                                                    )}
                                                </Fab>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Card>
                            </Grid>

                            {/* Cancel Reservation Section */}
                            {reservation?.reservation_status === 'Booked' && (
                                <Grid size={12}>
                                    {!showCancelConfirmation ? (
                                        <Button
                                            variant="contained"
                                            color="error"
                                            endIcon={<Close />}
                                            sx={{
                                                mt: 2,
                                                height: 56,
                                                borderRadius: theme.borderRadius.medium,
                                                width: '100%'
                                            }}
                                            onClick={() => setShowCancelConfirmation(true)}
                                            disabled={isLoading}
                                        >
                                            Cancel Reservation
                                        </Button>
                                    ) : (
                                        <Box
                                            sx={{
                                                mt: 2,
                                                border: '2px solid red',
                                                borderRadius: theme.borderRadius.medium,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                p: 2,
                                            }}
                                        >
                                            <Typography color="error" fontWeight="bold">
                                                Are you sure you want to cancel this reservation?
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <IconButton
                                                    sx={{
                                                        bgcolor: '#00c4b4',
                                                        color: 'white',
                                                        '&:hover': { bgcolor: '#00a69c' },
                                                    }}
                                                    onClick={handleCancelReservation}
                                                >
                                                    <CheckIcon />
                                                </IconButton>
                                                <IconButton
                                                    sx={{
                                                        bgcolor: 'error.main',
                                                        color: 'white',
                                                        '&:hover': { bgcolor: 'error.dark' },
                                                    }}
                                                    onClick={() => setShowCancelConfirmation(false)}
                                                >
                                                    <Close />
                                                </IconButton>
                                            </Box>
                                        </Box>
                                    )}
                                </Grid>
                            )}
                        </Grid>
                    </DialogContent>
                </Box>
            </Dialog>

            {/* Action Confirmation Dialog */}
            <Dialog
                open={openActionDialog}
                onClose={() => setOpenActionDialog(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                    },
                }}
            >
                <DialogTitle id="alert-dialog-title">
                    {pendingAction === 'checkIn' ? 'Confirm Check In' : 'Confirm Check Out'}
                </DialogTitle>
                <DialogContent>
                    {getDialogContent()}
                </DialogContent>
                <DialogActions sx={{ mb: 2 }}>
                    <CustomButton
                        variant="outlined"
                        sx={{
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            borderRadius: theme.borderRadius.medium,
                            border: "2px solid var(--Primary-Blue-MB, #27394D)",
                            textTransform: 'none',
                            '&:hover': { borderColor: 'primary.dark' },
                            px: 1.5,
                            py: 0.75,
                            minWidth: '120px',
                            flexShrink: 0,
                        }}
                        onClick={() => setOpenActionDialog(false)}
                    >
                        Cancel
                    </CustomButton>
                    <CustomButton
                        variant="outlined"
                        sx={{
                            backgroundColor: "primary.main",
                            color: 'light.main',
                            borderRadius: theme.borderRadius.medium,
                            textTransform: 'none',
                            '&:hover': { borderColor: 'primary.dark' },
                            px: 1.5,
                            py: 0.75,
                            minWidth: '150px',
                            flexShrink: 0,
                        }}
                        onClick={handleConfirmation}
                    >
                        Confirm
                    </CustomButton>
                </DialogActions>
            </Dialog>
        </>
    );
}